namespace Portico.Services {
	import Inject = Portico.Decorators.Inject;
	import Service = Portico.Decorators.Service;
	import models = Portico.Models;
	import pme = Portico.Models.Enterprise;
	import psga = apigateway.client;
	import psgm = apigateway.models;

	export interface IAuthenticationService {
		authenticateAccount(userName: string, password: string): angular.IPromise<number>;
		authenticateUser(userName: string, password: string): angular.IPromise<models.AuthenticationResponse>;
		changePassword(oldPassword: string, newPassword: string): angular.IPromise<number>;
		createSession(): angular.IPromise<models.AuthenticationResponse>;
		createUser(): angular.IPromise<pme.User>;
		getCurrentUser(): angular.IPromise<pme.User>;
		getPortalRoles(portalId: number): angular.IPromise<string[]>;
		getPortalUsers(portalId: number, accountId?: number): angular.IPromise<pme.User[]>;
		hasSession(): boolean;
        isAuthenticated(): boolean;
		isAuthorized(features: string[]): boolean;
		isEmailRegistered(email: string): angular.IPromise<boolean>;
		isExistingUser(userName: string): angular.IPromise<models.AuthenticationResponse>;
		isExpiring(): boolean;
		logOut(): angular.IPromise<void>;
		resetPassword(userName: string): angular.IPromise<number>;
		resetUserPassword(portalId: string, userId: number): angular.IPromise<boolean>;
		saveCurrentUser(user: pme.User): angular.IPromise<boolean>;
		saveUser(portalId: string, user: pme.User): angular.IPromise<boolean>;
		touchSession(): ng.IPromise<models.AuthenticationResponse>;
	}

	@Service('app', 'authenticationService')
	@Inject('$http', '$q', '$window', 'storageService', 'dataService', 'modalService', 'serverConstants', 'porticoEvents', 'sessionService', 'EdgeAuthenticationApi', 'dateService')
	export class AuthenticationService implements IAuthenticationService {
		private jwtTokenName;
		private sessionKeyName;

		constructor(protected $http: ng.IHttpService,
			protected $q: ng.IQService,
			protected $window: ng.IWindowService,
			protected storageService: Portico.Services.IStorageService,
			protected dataService: Portico.Services.IDataService,
			protected modalService: Portico.Services.IModalService,
			protected serverConstants: Portico.Models.Interfaces.IServerConstants,
			protected porticoEvents: Portico.Services.IPorticoEvents,
			protected sessionService: Portico.Services.ISessionService,
			protected edgeAuthenticationApi: psga.EdgeAuthenticationApi,
            protected dateService: Portico.Services.IDateService
		) {
			this.$http.defaults.headers.common['accept'] = 'application/json';
			this.$http.defaults.headers.common["X-Requested-With"] = 'XMLHttpRequest';
			this.jwtTokenName = this.dataService.jwtTokenKeyName;
			this.sessionKeyName = this.dataService.sessionKeyName;
		}

		// ***** Public methods

		authenticateAccount(userName: string, password: string): angular.IPromise<number> {
			const authenticateLoginAccountRequest = {
				portalId: this.getPortalId(),
				userName: userName,
				password: password
			};

			return this.$q.when(this.edgeAuthenticationApi.authenticationAuthenticateUser(authenticateLoginAccountRequest).then((response) => {
				return response.data.data;
			}));
		}

		authenticateUser(userName: string, password: string): angular.IPromise<models.AuthenticationResponse> {
			const request = this.buildRequest(models.AuthenticationAction.AuthenticateAndCreateSession);

			request.userName = userName;
			request.password = password;

			return this.authenticate(request).then((response: angular.IHttpPromiseCallbackArg<models.AuthenticationResponse>) => {
				this.dataService.session.authentication = response.data;
				return response.data;
			});
		}

		changePassword(oldPassword: string, newPassword: string): angular.IPromise<number> {
			const changeLoginRequest = {
				oldPassword: oldPassword,
				newPassword: newPassword
			};

			return this.$q.when(this.edgeAuthenticationApi.authenticationChangePassword(changeLoginRequest).then((response) => {
				return response.data.data;
			}));
		}

		createSession(): angular.IPromise<models.AuthenticationResponse> {
			//const session = this.sessionService.getExistingSession();

			//if (session !== null) {
			//	// first we need to check if the user already has an authenticated session so that we don't overwrite that
			//	if (session.userName !== null) {
			//		return this.$q.resolve(null);
			//	}
			//}

			const request = this.buildRequest(models.AuthenticationAction.CreateSession);

			return this.authenticate(request).then((response: angular.IHttpPromiseCallbackArg<models.AuthenticationResponse>) => {
				return response.data;
			});
		}

		createUser(): angular.IPromise<pme.User> {
			const user = new pme.User();
			return this.$q.when(user);
		}

		getCurrentUser(): angular.IPromise<pme.User> {
			return this.$q.when(this.edgeAuthenticationApi.authenticationGetCurrentUser({}).then((response) => {
				return new pme.User(response.data.data);
			}));
		}

		getPortalRoles(portalId: number): angular.IPromise<string[]> {
			let endpoint = '/api/PortalRole/' + portalId;

			return this.$http.get<any>(endpoint).then((response) => {
				return response.data;
			});
		}

		getPortalUsers(portalId: number, accountId?: number): angular.IPromise<pme.User[]> {
			let endpoint = '/api/PortalUser/' + portalId;

			if (accountId) {
				endpoint += '/' + accountId;
			}

			return this.$http.get<any>(endpoint).then((response) => {
				return _.map(response.data, (u) => {
					return new pme.User(u);
				});
			});
		}

		hasSession(): boolean {
			const sessionKey = `session-${this.serverConstants.portalId}`;
			const sessionDetail = this.storageService.getItem(models.StorageType.Session, sessionKey) as models.SessionDetail;
			return sessionDetail !== null && _.get(sessionDetail, 'expirationDate', null) !== null &&
				this.dateService.isBefore(this.dateService.subtract(sessionDetail.expirationDate, 1, 'minutes'));
		}

		isAuthenticated(): boolean {
			const sessionKey = `session-${this.serverConstants.portalId}`;
			const sessionDetail = this.storageService.getItem(models.StorageType.Session, sessionKey) as models.SessionDetail;
			return this.hasSession() && _.get(sessionDetail, 'userName', null) !== null;
		}

		isAuthorized(features: string[]): boolean {
			const sessionKey = `session-${this.serverConstants.portalId}`;
			const sessionDetail = this.storageService.getItem(models.StorageType.Session, sessionKey) as models.SessionDetail;
			const sessionFeatures = _.get(sessionDetail, 'features', <string[]>[]).concat(['Authenticated']);
			return this.isAuthenticated() && _.difference(features, sessionFeatures).length === 0;
		}

		isEmailRegistered(email: string): angular.IPromise<boolean> {
			return this.$q.when(this.edgeAuthenticationApi.authenticationIsEmailRegistered({ email }).then((response) => {
				return response.data.data;
			}));
		}

		isExistingUser(userName: string): angular.IPromise<models.AuthenticationResponse> {
			const request = this.buildRequest(models.AuthenticationAction.IsExistingUser);

			request.userName = userName;

			return this.$http.post<models.AuthenticationResponse>('/api/Authentication', request).then((response) => {
				return response.data;
			});
		}

		isExpiring(): boolean {
			const warning = this.dataService.getReferenceId('session-warning', 10);
			const sessionKey = `session-${this.serverConstants.portalId}`;
			const sessionDetail = this.storageService.getItem(models.StorageType.Session, sessionKey) as models.SessionDetail;

			const a = this.dateService.isAfter(this.dateService.subtract(sessionDetail.expirationDate, warning + 1, 'minutes'));
			const b = this.dateService.isBefore(this.dateService.subtract(sessionDetail.expirationDate, warning, 'minutes'));

			return sessionDetail !== null && _.get(sessionDetail, 'expirationDate', null) !== null && a && b;
		}

		logOut(): angular.IPromise<void> {
			this.dataService.session.authentication = null;
			this.sessionService.wipeLocalSessions();

			return this.createSession().then(() => {
				this.porticoEvents.loggedOutEvent();
			});
		}

		resetPassword(userName: string): angular.IPromise<number> {
			const request = {
				portalId: this.getPortalId(),
				userName: userName
			};

			return this.$q.when(this.edgeAuthenticationApi.authenticationResetPassword(request).then((response) => {
				return response.data.data;
			}));
		}

		resetUserPassword(portalId: string, userId: number): angular.IPromise<boolean> {
			return this.$http.post<any>('/api/Password/' + portalId + '/' + userId, null).then((response) => {
				return true;
			});
		}

		saveCurrentUser(user: pme.User): angular.IPromise<boolean> {
			return this.$q.when(this.edgeAuthenticationApi.authenticationSaveCurrentUser(user).then((response) => {
				return true;
			}));
		}

		saveUser(portalId: string, user: pme.User): angular.IPromise<boolean> {
			return this.$http.put<any>('/api/PortalUser/' + portalId, user).then((response) => {
				return true;
			});
		}

		touchSession(): ng.IPromise<models.AuthenticationResponse> {
			const session = this.sessionService.getExistingSession();
			const request = this.buildRequest(models.AuthenticationAction.TouchSession);

			request.jwt = this.sessionService.getJwt();
			request.isParent = _.get(session, 'isParent', false);

			// return value if the session is invalid
			const expiredResponse = {
				data: {
					success: true,
					status: Models.PorticoAuthenticateStatus.SessionExpired,
					jwtToken: null,
					messages: [],
					session: null,
					sessionDetail: null
				} as models.AuthenticationResponse
			} as angular.IHttpPromiseCallbackArg<models.AuthenticationResponse>;

			const promise = request.portalId && request.jwt ?
				this.$http.post<models.AuthenticationResponse>('/api/Authentication', request) :
				this.$q.when(expiredResponse);

			return promise.then((response: angular.IHttpPromiseCallbackArg<models.AuthenticationResponse>) => {
				if (_.get(response, 'data.status', models.PorticoAuthenticateStatus.SessionExpired) as models.PorticoAuthenticateStatus === models.PorticoAuthenticateStatus.SessionValid) {
					const newSession = _.get(response, 'data.sessionDetail', null) as models.SessionDetail;
					this.sessionService.updateCurrentSessionExpiration(newSession.expirationDate);
				} else {
					this.logOut();
				}

				return _.get(response, 'data', null);
			});
		}

		// ***** Private methods
		private authenticate(request: models.AuthenticationRequest): ng.IPromise<ng.IHttpPromiseCallbackArg<models.AuthenticationResponse>> {
			// this.modalService.show('spinner');
			document.getElementById('global-spinner-overlay').style.display = 'flex';

			const promise = request.portalId ?
				this.$http.post<models.AuthenticationResponse>('/api/Authentication', request) :
				this.$q.when(null);

			return promise.then((response: angular.IHttpPromiseCallbackArg<models.AuthenticationResponse>) => {
				if (response.data.sessionDetail) {
					response.data.sessionDetail.status = Models.SESSION_ACTIVE;
					this.sessionService.saveNewSession(response.data.jwtToken, response.data.sessionDetail, request, response.data.childSessionData);
				}

				return response;
			}).catch(() => {
				const authResponse = {
					success: false,
					status: models.PorticoAuthenticateStatus.NotAuthenticated,
					jwtToken: null,
					childSessionData: null,
					messages: null,
					session: null,
					sessionDetail: null
				} as models.AuthenticationResponse;

				return this.$q.resolve({ data: authResponse } as angular.IHttpPromiseCallbackArg<models.AuthenticationResponse>);
			}).finally(() => {
				// this.modalService.hide('spinner');
                document.getElementById('global-spinner-overlay').style.display = 'none';
			});
		}

		private buildRequest(action: models.AuthenticationAction): models.AuthenticationRequest {
			let request = new models.AuthenticationRequest();

			request.password = null;
			request.userName = null;
			request.action = action;
			request.portalId = this.getPortalId();

			return request;
		}

		private getPortalId(): string {
			return _.get(this, 'dataService.setup.portal.id', null);
		}
	}
}